import styles from './Pagination.module.scss';
import Arrow from '@src/assets/icons/outline/chevron-down.svg?react';
import cn from 'classnames';
import { SubTitle3, Title3 } from '@components/new/Typo';
import Dropdown, { DropdownOptionProps, DropdownValueProps } from '../Dropdown';
import ReactPaginate from 'react-paginate';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

const ValueComponent = ({ id, isOpen }: DropdownValueProps<number>) => (
  <div className={styles.sizeValue}>
    <Title3>{id}</Title3>
    <Arrow
      className={cn(
        styles.sizeValueIcon,
        isOpen && styles.sizeValueIconOpen,
      )}
    />
  </div>
)

const OptionComponent = ({ id }: DropdownOptionProps<number>) => (
  <div className={styles.sizeOption}>
    <Title3>{id}</Title3>
  </div>
)

interface PaginationProps {
  className?: string;
  currentPage: number;
  totalItems: number;
  pageSize: number;
  onChange: (current: number, size: number) => void;
  onSizeChange: (current: number, size: number) => void;
  hidePaginationDropdown?: boolean;
  isNew?: boolean; // todo
  pageSizeOptions?: number[];
}

const Pagination: React.FC<PaginationProps> = ({
  className,
  currentPage,
  totalItems,
  pageSize,
  onChange,
  onSizeChange,
  hidePaginationDropdown,
  isNew,
  pageSizeOptions,
}) => {
  const { t } = useTranslation();

  const optionsArray = useMemo(() => pageSizeOptions ?? [11, 20, 50], [pageSizeOptions]);
  const options = useMemo(() => optionsArray.map(v => ({ id: v })), [optionsArray]);

  if (isNew) {
    return (
      <div className={cn(className, styles.root)}>
        <div className={styles.leftBlock}>
          {hidePaginationDropdown ? null : (
            <Dropdown<number>
              onChange={(v) => onSizeChange(1, v)}
              OptionComponent={OptionComponent}
              ValueComponent={ValueComponent}
              defaultOption={{ id: pageSize || (optionsArray[0] ?? 12) }}
              options={options}
              menuPosition="top"
              menuAlign="right"
            />
          )}
        </div>
        <ReactPaginate
          className={cn(className, styles.pagination)}
          pageCount={Math.ceil(totalItems / pageSize)}
          pageRangeDisplayed={2}
          marginPagesDisplayed={2}
          previousLabel={(<Arrow className={styles.prevIcon} />)}
          nextLabel={(<Arrow className={styles.nextIcon} />)}
          breakLabel={(<SubTitle3>...</SubTitle3>)}
          // onPageChange={(v) => onChange(v.selected + 1, pageSize)}
          onPageChange={(v) => {
            onChange(v.selected + 1, pageSize);
          }}
          forcePage={currentPage - 1}
          pageClassName={styles.page}
          previousClassName={styles.prev}
          nextClassName={styles.next}
          disabledClassName={styles.disabled}
          activeClassName={styles.active}
          breakClassName={styles.break}
        />
      </div>
    )
  }

  return (
    <div className={cn(className, styles.root)}>
      <ReactPaginate
        className={cn(className, styles.pagination)}
        pageCount={Math.ceil(totalItems / pageSize)}
        pageRangeDisplayed={2}
        marginPagesDisplayed={2}
        previousLabel={(<Arrow className={styles.prevIcon} />)}
        nextLabel={(<Arrow className={styles.nextIcon} />)}
        breakLabel={(<SubTitle3>...</SubTitle3>)}
        onPageChange={(v) => {
          onChange(v.selected + 1, pageSize);
        }}
        forcePage={currentPage - 1}
        pageClassName={styles.page}
        previousClassName={styles.prev}
        nextClassName={styles.next}
        disabledClassName={styles.disabled}
        activeClassName={styles.active}
        breakClassName={styles.break}
      />
      <div className={styles.rightBlock}>
        {hidePaginationDropdown ? null : (
          <Dropdown<number>
            onChange={(v) => onSizeChange(1, v)}
            OptionComponent={OptionComponent}
            ValueComponent={ValueComponent}
            defaultOption={{ id: pageSize || 10 }}
            options={[{ id: 10 }, { id: 20 }, { id: 50 }]}
            menuPosition="top"
            menuAlign="right"
          />
        )}
        <SubTitle3>
          {t("pagination.info", {
            from: (currentPage - 1) * pageSize + 1,
            to: Math.min(totalItems, currentPage * pageSize),
            total: totalItems,
          })}
        </SubTitle3>
      </div>

    </div>
  );
};

export default Pagination;
